import {
  Badge,
  EmptyState,
  FlexBox,
  Icon,
  InputSelectionListProps,
  SelectionList,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { QuickView, ReadQuickViewsApiArg } from 'lib-analytics-types';
import {
  HookFormFieldWrappedBaseProps,
  HookFormSelectionList,
} from 'lib-shared-hook-form';
import { PropsWithChildren } from 'lib-shared-types-utils';
import { ComponentProps, useCallback, useMemo, useState } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';
import { useMount, useUpdateEffect } from 'react-use';
import { Waypoint } from 'react-waypoint';

import { analyticsApi } from 'apis/analytics';
import { useLazyPaginatedEndpoint } from 'utils/apis/pagination/useLazyPaginatedEndpoint';

import styles from './quickViewSelectionList.module.scss';

type QuickViewSelectionListProps = {
  /**
   * Whether to load the initial list of quick views on mount (`lazy: false`) or
   * when the dropdown is opened for the first time (`lazy: true`).
   */
  lazy?: boolean;
} & Pick<ReadQuickViewsApiArg, 'type'> &
  Partial<InputSelectionListProps<QuickView>>;

export const QuickViewSelectionList = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  children,
  ...props
}: HookFormFieldWrappedBaseProps<
  TFieldValues,
  TName,
  QuickViewSelectionListProps
> &
  PropsWithChildren): JSX.Element => {
  const { type, lazy = true, ...componentProps } = props.componentProps ?? {};

  const {
    arg,
    asyncStatus,
    items: quickViews,
    changeArg,
    search,
    hasNextPage,
    nextPage,
  } = useLazyPaginatedEndpoint({
    endpoint: analyticsApi.endpoints.readQuickViews,
    itemsSelector: ({ data }) => data.quick_views,
    infinite: true,
  });

  const initialArg = useMemo<ReadQuickViewsApiArg>(
    () => ({
      type,
      sort: { key: 'name' },
    }),
    [type],
  );

  const [searchTerm, setSearchTerm] = useState('');

  useMount(() => {
    if (!lazy) {
      changeArg(initialArg, { debounceTime: 0 });
    }
  });

  useUpdateEffect(() => {
    search(searchTerm, { debounceTime: 300 });
  }, [search, searchTerm]);

  const handleToggleDropdown = useCallback(
    (newIsOpen: boolean): void => {
      // If the dropdown is closed, reset the search term
      if (!newIsOpen) {
        setSearchTerm('');
      }

      // If the dropdown is opened and lazy loading is enabled, load the initial list
      if (lazy && newIsOpen && !quickViews.length) {
        changeArg(initialArg, { debounceTime: 0 });
      }
    },
    [changeArg, initialArg, lazy, quickViews.length],
  );

  const inputSelectionListProps = useMemo(
    (): ComponentProps<
      typeof HookFormSelectionList<QuickView>
    >['componentProps'] => ({
      placeholder: 'Select a dashboard',
      equalityFn: (a, b) => a.id === b.id,
      selectionListProps: {
        className: styles.selectionList,
      },
      filter: {
        value: searchTerm,
        onChange: (e) => setSearchTerm(e.target.value),
      },
      getItemTitleByValue: (quickView) => quickView?.name ?? '',
      onToggleDropdown: handleToggleDropdown,
      ...componentProps,
    }),
    [componentProps, handleToggleDropdown, searchTerm],
  );

  return (
    <HookFormSelectionList {...props} componentProps={inputSelectionListProps}>
      {quickViews.map((quickView) => {
        return (
          <SelectionList.Item
            key={quickView.id}
            value={quickView}
            contentClassName={styles.selectionListItemContent}
          >
            <Icon
              name={quickView.favorited ? 'star-full' : 'star'}
              color={
                quickView.favorited ? colors.orange[300] : colors.neutral[400]
              }
            />

            <div>
              <Text.Paragraph>{quickView.name}</Text.Paragraph>

              {quickView.description && (
                <Text.Paragraph variant="4" subdued>
                  {quickView.description}
                </Text.Paragraph>
              )}
            </div>

            {quickView.shared && (
              <Badge variant="secondary" size="small">
                Public
              </Badge>
            )}
          </SelectionList.Item>
        );
      })}

      {asyncStatus === 'pending' && (
        <FlexBox as="li" padding="300" justifyContent="center">
          <Spinner size={20} />
        </FlexBox>
      )}

      {asyncStatus === 'fulfilled' && !quickViews.length && (
        <FlexBox as="li" padding="300" justifyContent="center">
          <EmptyState
            title={
              arg?.searchTerm ? 'No dashboard found' : 'No dashboards available'
            }
            description={
              arg?.searchTerm
                ? 'Try changing your search criteria.'
                : 'Click on + above to create your first saved dashboard.'
            }
          />
        </FlexBox>
      )}

      {asyncStatus === 'fulfilled' && hasNextPage && (
        <Waypoint onEnter={nextPage} />
      )}
    </HookFormSelectionList>
  );
};
